import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import TopicIllustration, { TopicVariant } from '../components/TopicIllustration';
import CountdownModal from '../components/CountdownModal';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getParentTopicDisplayName } from '../utils/categoryGrouping';
import { isTopicCompletedForDate, markTopicCompleted } from '../utils/quizCompletionCookie';
import { ensureCsrfToken, getCsrfHeader } from '../utils/csrf';

interface Question {
  id: string;
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  category: string;
  parentTopic?: string;
  difficulty: string;
}

interface QuizAnswer {
  questionId: string;
  selectedAnswer: string;
}

const TOPIC_NAMES: Record<number, string> = {
  1: 'Pop Culture',
  2: 'Science & Nature',
  3: 'History & Politics',
  4: 'Geography & World',
  5: 'Sports & Games',
};

const TOPIC_VARIANTS: Record<number, TopicVariant> = {
  1: 'pop-culture',
  2: 'science-nature',
  3: 'history-politics',
  4: 'geography-world',
  5: 'sports-games',
};

const MIN_TIME_PER_QUESTION_MS = 500;

const Quiz: React.FC = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [searchParams] = useSearchParams();
  const topicSlot = Math.min(5, Math.max(1, parseInt(searchParams.get('topic') || '1', 10) || 1));
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quizStarted, setQuizStarted] = useState(false);
  const [startTime, setStartTime] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [questionStartTimestamp, setQuestionStartTimestamp] = useState<number>(0);
  const questionTimeSpentRef = useRef<Record<string, number>>({});
  const previousPathnameRef = useRef<string>('');
  const [tabOutCount, setTabOutCount] = useState(0);
  const [showTabOutWarning, setShowTabOutWarning] = useState(false);
  const [quizInvalidated, setQuizInvalidated] = useState(false);
  const [festivity, setFestivity] = useState<{ id: string; name: string; description: string | null } | null>(null);
  const [quizDate, setQuizDate] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  useDocumentTitle('Cognidaily | Quiz');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authLoading) return;
    // Guest: check cookie first – if already completed, block
    if (!isAuthenticated && isTopicCompletedForDate(topicSlot)) {
      setError('You have already completed this topic today. Create an account to save your progress and compete on the leaderboard.');
      setLoading(false);
      return;
    }
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, topicSlot]);


  // Scroll to top immediately when pre-quiz screen is shown
  useLayoutEffect(() => {
    if (!quizStarted) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [quizStarted]);

  // Fallback scroll after render to catch any layout shifts
  useEffect(() => {
    if (!quizStarted) {
      const scrollToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };
      
      scrollToTop();
      // Also scroll after a short delay to catch any async content loading
      const timeoutId = setTimeout(scrollToTop, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [quizStarted]);

  // Initialize previous pathname ref
  useEffect(() => {
    if (!previousPathnameRef.current) {
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Hide navbar when quiz is active (mobile)
  useEffect(() => {
    const isQuizActive = quizStarted && !quizCompleted;
    if (isQuizActive) {
      document.body.classList.add('quiz-active');
    } else {
      document.body.classList.remove('quiz-active');
    }
    return () => {
      document.body.classList.remove('quiz-active');
    };
  }, [quizStarted, quizCompleted]);

  // Navigation detection for competitive quiz (anti-cheat)
  useEffect(() => {
    // Only apply navigation detection to main competitive quiz for authenticated users
    if (!quizStarted || quizCompleted || quizInvalidated || !isAuthenticated) {
      return;
    }

    // Check if user navigated away from quiz page
    if (previousPathnameRef.current === '/quiz' && location.pathname !== '/quiz') {
      // User navigated away from quiz - mark quiz as abandoned
      const markAbandoned = async () => {
        try {
          await axios.post('/api/quiz/abandon');
        } catch (err: any) {
          console.error('Failed to mark quiz as abandoned:', err);
        }
      };
      markAbandoned();
    }

    // Update previous pathname
    previousPathnameRef.current = location.pathname;

    // Cleanup: check on unmount if quiz is still active
    return () => {
      if (quizStarted && !quizCompleted && location.pathname === '/quiz') {
        // Component unmounting while quiz is active on quiz page - user likely navigated away
        const markAbandoned = async () => {
          try {
            await axios.post('/api/quiz/abandon');
          } catch (err: any) {
            console.error('Failed to mark quiz as abandoned on unmount:', err);
          }
        };
        markAbandoned();
      }
    };
  }, [location.pathname, quizStarted, quizCompleted, quizInvalidated, isAuthenticated]);

  // Blur/focus event detection for main quiz only (not for leagues or casual quizzes)
  useEffect(() => {
    // Only apply blur detection to main quiz for authenticated users
    if (!quizStarted || quizCompleted || quizInvalidated || !isAuthenticated) {
      return;
    }

    const handleBlur = () => {
      // User tabbed out
      const newCount = tabOutCount + 1;
      setTabOutCount(newCount);

      if (newCount === 1) {
        // First tab-out: show warning
        setShowTabOutWarning(true);
        // Hide warning after 5 seconds
        setTimeout(() => {
          setShowTabOutWarning(false);
        }, 5000);
      } else if (newCount >= 2) {
        // Second tab-out: invalidate quiz
        setQuizInvalidated(true);
        setShowTabOutWarning(false);
        setError('Your quiz has been invalidated due to multiple tab-out violations. You cannot complete today\'s quiz.');
      }
    };

    const handleFocus = () => {
      // User returned to tab - no action needed, but we could log this
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [quizStarted, quizCompleted, quizInvalidated, isAuthenticated, tabOutCount]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (quizStarted && !quizCompleted) {
      interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [quizStarted, quizCompleted]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      const response = await axios.get('/api/quiz/questions', {
        params: { topic: topicSlot },
        timeout: 10000,
      });

      if (response.data.success) {
        setQuestions(response.data.data.questions);
        setQuizDate(response.data.data?.date || '');
        // Set festivity if present
        if (response.data.data.festivity) {
          setFestivity(response.data.data.festivity);
        } else {
          setFestivity(null);
        }
        questionTimeSpentRef.current = {};
        setAnswers([]);
        setQuizStarted(false);
        setQuizCompleted(false);
        setResults(null);
        setStartTime(0);
        setQuestionStartTimestamp(0);
        setCurrentQuestionIndex(0);
        setError('');
      } else {
        setError(response.data.message || 'Failed to load questions');
      }
    } catch (err: any) {
      console.error('Quiz loading error:', err.response?.data || err.message);
      let errorMessage = 'Failed to load questions';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.response?.status === 409) {
        errorMessage = 'You have already completed this topic today. Come back tomorrow for new questions.';
        setQuizCompleted(true);
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (err.response?.status === 503) {
        errorMessage = 'Today\'s quiz is still being prepared. Please try again in a few minutes.';
      } else if (err.response?.status === 404) {
        errorMessage = 'No questions available for today. Please try again later.';
      } else if (err.response?.status === 401) {
        // Authentication required - redirect to login
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/login?returnUrl=${returnUrl}`);
        return;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const recordTimeForCurrentQuestion = (resetStart: boolean = true) => {
    if (!quizStarted || quizCompleted) return;
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion || !questionStartTimestamp) return;

    const now = Date.now();
    const elapsed = Math.max(0, now - questionStartTimestamp);

    if (elapsed > 0) {
      const previous = questionTimeSpentRef.current[currentQuestion.id] || 0;
      questionTimeSpentRef.current = {
        ...questionTimeSpentRef.current,
        [currentQuestion.id]: previous + elapsed
      };
    }

    if (resetStart) {
      setQuestionStartTimestamp(now);
    }
  };

  const goToQuestion = (targetIndex: number) => {
    if (!quizStarted || quizCompleted) return;
    if (targetIndex === currentQuestionIndex) return;
    if (targetIndex < 0 || targetIndex >= questions.length) return;

    recordTimeForCurrentQuestion();
    setCurrentQuestionIndex(targetIndex);
  };

  const startQuiz = () => {
    setShowCountdown(true);
  };

  const handleCountdownComplete = () => {
    setShowCountdown(false);
    actualStartQuiz();
  };

  const actualStartQuiz = async () => {
    // Questions will load in the background if not already loaded
    const now = Date.now();
    questionTimeSpentRef.current = {};
    setAnswers([]);
    setQuizStarted(true);
    setQuizCompleted(false);
    setResults(null);
    setStartTime(now);
    setCurrentTime(now);
    setQuestionStartTimestamp(now);
    setCurrentQuestionIndex(0);
    setError('');
    setTabOutCount(0);
    setShowTabOutWarning(false);
    setQuizInvalidated(false);
    
    // Mark quiz as started (competitive quiz only, not league)
    if (isAuthenticated) {
      try {
        await axios.post('/api/quiz/start', { topicSlot });
      } catch (err: any) {
        console.error('Failed to mark quiz as started:', err);
        // Don't block quiz start if this fails
      }
    }
    
    // If questions aren't loaded yet, trigger a reload
    if (questions.length === 0 && !loading) {
      loadQuestions();
    }
  };

  const selectAnswer = (answer: string) => {
    if (quizCompleted) return;
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    setError('');

    setAnswers((prev) => {
      const existingIndex = prev.findIndex((a) => a.questionId === currentQuestion.id);
      const updatedAnswer: QuizAnswer = {
        questionId: currentQuestion.id,
        selectedAnswer: answer
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = updatedAnswer;
        return updated;
      }

      return [...prev, updatedAnswer];
    });

    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < questions.length) {
      window.setTimeout(() => {
        goToQuestion(nextIndex);
      }, 150);
    }
  };

  const submittingRef = useRef(false);
  const submitQuiz = async () => {
    if (!quizStarted || quizCompleted) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    const answeredQuestionIds = new Set(answers.map((answer) => answer.questionId));
    if (answeredQuestionIds.size !== questions.length) {
      setError('Please answer all questions before submitting');
      submittingRef.current = false;
      return;
    }

    recordTimeForCurrentQuestion(false);

    const now = Date.now();
    setSubmitting(true);

    try {
      const completionTime = now - startTime;
      const answerMap = new Map(answers.map((answer) => [answer.questionId, answer.selectedAnswer]));

      const payloadAnswers = questions.map((question) => ({
        questionId: question.id,
        selectedAnswer: answerMap.get(question.id) as string,
        timeSpent: Math.max(
          MIN_TIME_PER_QUESTION_MS,
          Math.round(questionTimeSpentRef.current[question.id] || 0)
        )
      }));

      const totalRecordedTime = payloadAnswers.reduce((sum, item) => sum + item.timeSpent, 0);
      const completionTimeToSend = Math.max(completionTime, totalRecordedTime);

      // Prepare submission payload
      const submissionPayload: any = {
        answers: payloadAnswers,
        completionTime: completionTimeToSend,
        topicSlot,
      };

      submissionPayload.quizType = 'main';
      if (quizDate) {
        submissionPayload.quizDate = quizDate;
      }
      if (isAuthenticated) {
        submissionPayload.tabOutCount = tabOutCount;
      }

      let response: any;
      if (isAuthenticated) {
        response = await axios.post('/api/quiz/submit', submissionPayload);
      } else {
        await ensureCsrfToken();
        response = await axios.post('/api/quiz/submit-guest', submissionPayload, {
          headers: getCsrfHeader(),
        });
      }

      if (response.data.success) {
        const resultData = response.data.data;
        setResults(resultData);
        setQuizCompleted(true);

        if (isAuthenticated) {
          // Store XP data in sessionStorage for the Share page to display
          if (resultData.xpEarned !== undefined && resultData.totalXP !== undefined && resultData.currentLevel !== undefined) {
            if (resultData.levelUp) {
              const levelUpDataToStore = {
                oldLevel: resultData.levelUp.oldLevel,
                newLevel: resultData.levelUp.newLevel,
                xpEarned: resultData.xpEarned,
                totalXP: resultData.totalXP,
                xpToNextLevel: resultData.levelUp.xpToNextLevel,
                progressToNextLevel: resultData.levelUp.progressToNextLevel
              };
              sessionStorage.setItem('quizLevelUp', JSON.stringify(levelUpDataToStore));
            } else {
              const getXPForLevel = (level: number): number => {
                if (level <= 1) return 0;
                return Math.floor(400 * Math.pow(level, 1.5));
              };
              const safeLevel = Math.max(1, resultData.currentLevel);
              const currentLevelXP = getXPForLevel(safeLevel);
              const nextLevelXP = getXPForLevel(safeLevel + 1);
              const xpInCurrentLevel = resultData.totalXP - currentLevelXP;
              const xpNeededForNextLevel = nextLevelXP - currentLevelXP;
              const xpToNextLevel = nextLevelXP - resultData.totalXP;
              let progress: number;
              if (xpNeededForNextLevel > 0) {
                progress = Math.min(1, Math.max(0, xpInCurrentLevel / xpNeededForNextLevel));
              } else {
                progress = 0;
              }
              const xpDataToStore = {
                xpEarned: resultData.xpEarned,
                currentLevel: resultData.currentLevel,
                totalXP: resultData.totalXP,
                xpToNextLevel: Math.max(0, xpToNextLevel),
                progressToNextLevel: progress
              };
              sessionStorage.setItem('quizXPData', JSON.stringify(xpDataToStore));
            }
          }
          if (resultData.newlyAwardedBadges && resultData.newlyAwardedBadges.length > 0) {
            sessionStorage.setItem('quizNewBadges', JSON.stringify(resultData.newlyAwardedBadges));
          }
          try {
            const shareResponse = await axios.post('/api/quiz/share', { resultId: resultData.resultId });
            if (shareResponse.data.success && shareResponse.data.data.token) {
              navigate(`/share/${shareResponse.data.data.token}`);
            } else {
              setError('Failed to generate share link. Your quiz was submitted successfully. You can view your results on your profile.');
              setQuizCompleted(false);
            }
          } catch (shareErr: any) {
            console.error('Share link generation error:', shareErr.response?.data || shareErr.message);
            setError('Failed to generate share link. Your quiz was submitted successfully. You can view your results on your profile.');
            // Keep quizCompleted true – quiz was submitted; show profile/compete links
          }
        } else {
          // Guest: store completion in cookie
          markTopicCompleted(topicSlot, resultData.questionsCorrect ?? 0);
        }
      } else {
        setError(response.data.message || 'Failed to submit quiz');
      }
    } catch (err: any) {
      console.error('Quiz submission error:', err.response?.data || err.message);
      if (err.response?.status === 401 && isAuthenticated) {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/login?returnUrl=${returnUrl}`);
        return;
      }
      if (err.response?.status === 409) {
        // Already completed (e.g. duplicate submit) – treat as success, go to Compete
        setQuizCompleted(true);
        navigate('/compete', { replace: true });
        return;
      }
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const getCurrentAnswer = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const answer = answers.find((a) => a.questionId === currentQuestion?.id);
    return answer?.selectedAnswer || '';
  };

  const getProgressPercentage = () => {
    return ((currentQuestionIndex + 1) / questions.length) * 100;
  };

  const formatTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };



  if (loading && !quizStarted && !error) {
    return <main><p>Loading today&apos;s set...</p></main>;
  }

  if (error && !quizStarted && !loading) {
    const isAlreadyCompleted = error.includes('already completed');
    return (
      <main>
        <h2>{isAlreadyCompleted ? 'Already played' : 'Quiz unavailable'}</h2>
        <p>{error}</p>
        {isAlreadyCompleted ? (
          <>
            <Link to="/register" className="btn btn-primary">Create account</Link>
            <p><Link to="/compete" className="link">Back to Compete</Link></p>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={loadQuestions}>Try again</button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Go home</button>
          </>
        )}
      </main>
    );
  }

  if (error && (!quizStarted || (quizCompleted && results))) {
    return (
      <main>
        <h2>{quizCompleted ? 'Quiz submitted' : 'Quiz unavailable'}</h2>
        <p>{error}</p>
        {quizCompleted && results ? (
          <>
            <button type="button" onClick={() => navigate('/profile')}>View profile</button>
            <button type="button" onClick={() => navigate('/leaderboard')}>View leaderboard</button>
          </>
        ) : (
          <>
            <button type="button" onClick={loadQuestions}>Reload questions</button>
            <button type="button" onClick={() => navigate('/leaderboard')}>View leaderboard</button>
          </>
        )}
      </main>
    );
  }

  if (authLoading) {
    return <main><p>Loading...</p></main>;
  }

  // Guest results view – show score and CTA to create profile
  if (quizCompleted && results && !isAuthenticated) {
    const qCorrect = results.questionsCorrect ?? 0;
    const total = results.totalQuestions ?? 10;
    const score = results.score ?? 0;
    return (
      <main className="quiz-results">
        <SEOHead
          title="Quiz Complete"
          description="You completed the quiz. Create an account to save your progress."
          url="https://cognidaily.io/quiz"
        />
        <section className="quiz-results__summary">
          <h1 className="quiz-results__title">Quiz complete</h1>
          <p className="quiz-results__score">
            <strong>{qCorrect}/{total}</strong> correct — <strong>{score}</strong> points
          </p>
        </section>
        <section className="quiz-results__cta">
          <h2 className="quiz-results__cta-title">Save your progress</h2>
          <p className="quiz-results__cta-desc">
            Create a free account to store your scores, earn XP, level up, and compete on the leaderboard.
          </p>
          <div className="quiz-results__actions">
            <Link to="/register" className="btn btn-primary btn-full btn--mobile-full">
              Create account
            </Link>
            <p className="quiz-results__sign-in">
              <Link to="/login">Already have an account? Sign in</Link>
            </p>
          </div>
        </section>
        <p className="quiz-results__back">
          <Link to="/compete">Back to Compete</Link>
        </p>
      </main>
    );
  }

  if (!quizStarted) {
    const topicVariant = TOPIC_VARIANTS[topicSlot] || 'pop-culture';
    return (
      <main>
        <CountdownModal isOpen={showCountdown} onComplete={handleCountdownComplete} />
        <SEOHead
          title={`${TOPIC_NAMES[topicSlot] || 'Quiz'} | Cognidaily`}
          description="10 multiple-choice questions. Test your knowledge."
          url={`https://cognidaily.io/quiz?topic=${topicSlot}`}
        />
        <section className="quiz-pre bento-section">
          <div className="bento-block">
            <div className="bento-block__image quiz-pre__image">
              <TopicIllustration variant={topicVariant} idSuffix={`quiz-pre-${topicSlot}`} />
            </div>
            <div className="bento-block__content">
              {festivity && (
                <p className="quiz-pre__festivity">
                  <strong>{festivity.name}</strong>
                  {festivity.description ? ` — ${festivity.description}` : ''}
                </p>
              )}
              <h1>{isAuthenticated && user?.username ? `Ready, ${user.username}?` : 'Ready?'}</h1>
              <h2>{TOPIC_NAMES[topicSlot] || 'Quiz'}</h2>
              <p>10 multiple-choice questions.</p>
              <p className="quiz-pre__warning">
                Stay on this page. Leaving this tab or switching to another app may invalidate your quiz.
              </p>
              <div className="quiz-pre__actions">
                <button type="button" className="btn btn-primary" onClick={startQuiz}>
                  Let&apos;s go!
                </button>
                <Link to="/compete" className="btn btn-secondary">
                  Back to Compete
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (quizStarted && questions.length === 0) {
    return <main><p>Loading questions...</p></main>;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = getCurrentAnswer();

  if (!currentQuestion) {
    return <main><p>Loading questions...</p></main>;
  }

  const categoryLabel = (() => {
    const parentTopicDisplay = getParentTopicDisplayName(currentQuestion.parentTopic);
    return parentTopicDisplay && parentTopicDisplay !== 'Other Topics'
      ? `${parentTopicDisplay} • ${currentQuestion.category}`
      : currentQuestion.category;
  })();

  return (
    <main className="quiz-interface">
      <SEOHead
        title="Daily Quiz"
        description="Take today's quiz and compete for the top spot on the leaderboard. 10 questions, one attempt, unlimited knowledge."
        keywords="daily quiz, trivia quiz, brain training, knowledge test, competitive quiz"
        url="https://cognidaily.io/quiz"
        noindex={true}
      />
      <div className="quiz-header">
        <div className="quiz-progress-bar">
          <div
            className="quiz-progress-bar__fill"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
        <div className="quiz-meta">
          <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
          <span className="quiz-meta__timer">{formatTime(currentTime - startTime)}</span>
        </div>
      </div>

      {showTabOutWarning && (
        <p className="quiz-alert quiz-alert--warning" role="alert">
          Stay on this page. Leaving again may invalidate your quiz.
        </p>
      )}
      {error && (
        <p className="quiz-alert quiz-alert--error" role="alert">{error}</p>
      )}

      <section className="quiz-question">
        <p className="quiz-question__category">
          <small>{categoryLabel}</small>
        </p>
        <h2 className="quiz-question__text">{currentQuestion.questionText}</h2>
        <ul className="quiz-options">
          {['A', 'B', 'C', 'D'].map((option) => {
            const optionText = currentQuestion[`option${option}` as keyof Question] as string;
            const isSelected = currentAnswer === option;
            return (
              <li key={option}>
                <button
                  type="button"
                  className={`quiz-option ${isSelected ? 'quiz-option--selected' : ''}`}
                  onClick={() => selectAnswer(option)}
                >
                  <span className="quiz-option__label">{option}.</span>
                  {optionText}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="quiz-nav">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => goToQuestion(currentQuestionIndex - 1)}
          disabled={currentQuestionIndex === 0}
        >
          Previous
        </button>
        {currentQuestionIndex < questions.length - 1 ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => goToQuestion(currentQuestionIndex + 1)}
            disabled={!currentAnswer}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={submitQuiz}
            disabled={submitting || answers.length !== questions.length}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        )}
      </div>
    </main>
  );
};

export default Quiz;



















